import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Spinner,
  VStack,
} from '@chakra-ui/react'
import { FavoritesProvider } from '@/contexts/FavoritesContext'
import { OrgDataProvider } from '@/contexts/OrgDataContext'
import { useAuth } from '@/contexts/AuthContext'
import { useAppDispatch, useAppSelector } from '@/store'
import { fetchFolderTree } from '@/store/slices/foldersSlice'
import { fetchFolderFiles } from '@/store/slices/filesSlice'
import { User } from '@/types'
import ReduxDataLoader from './ReduxDataLoader'
import DashboardLayoutPage from '@/pages/DashboardLayoutPage'
import DashboardContent from '@/pages/DashboardContent'
import SearchContent from '@/pages/SearchContent'
import RecentlyViewedContent from '@/pages/RecentlyViewedContent'
import TrashContent from '@/pages/TrashContent'
import FilePreviewContent from '@/pages/FilePreviewContent'
import AccountPage from '@/pages/AccountPage'
import AdminOrganizationsPage from '@/pages/AdminOrganizationsPage'
import CreateOrganizationPage from '@/pages/CreateOrganizationPage'
import OrganizationDetailsPage from '@/pages/OrganizationDetailsPage'
import AdminPlansPage from '@/pages/AdminPlansPage'
import AdminDashboardPage from '@/pages/AdminDashboardPage'
import OrganizationInvitationsPage from '@/pages/OrganizationInvitationsPage'
import GroupsPage from '@/pages/GroupsPage'
import SharedWithMePage from '@/pages/SharedWithMePage'
import FavoritesPage from '@/pages/FavoritesPage'
import NotFoundPage from '@/pages/NotFoundPage'
import HelpPage from '@/pages/HelpPage'

function createProtectedRoute(
  allowedRoles: User['role'][],
  user: User | null,
  path: string,
  element: React.ReactElement
) {
  if (!user || !allowedRoles.includes(user.role)) {
    return null
  }
  return <Route key={path} path={path} element={element} />
}

function InitialDataGate({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch()
  const foldersInitialized = useAppSelector((state) => state.folders.initialized)
  const foldersError = useAppSelector((state) => state.folders.error)

  // Gate the layout on the folder tree: the sidebar and most of the
  // authenticated shell depend on it. If the tree load fails (e.g. 500),
  // show an error state instead of a broken layout.
  if (!foldersInitialized) {
    return (
      <Center h="100vh">
        <Spinner size="xl" color="brand.500" />
      </Center>
    )
  }

  if (foldersError) {
    const handleRetry = () => {
      dispatch(fetchFolderTree())
      dispatch(fetchFolderFiles(null))
    }

    return (
      <Center h="100vh" p={6}>
        <VStack spacing={4} maxW="md">
          <Alert status="error" borderRadius="md" flexDirection="column" alignItems="flex-start">
            <Box display="flex" alignItems="center">
              <AlertIcon />
              <AlertTitle>Something went wrong</AlertTitle>
            </Box>
            <AlertDescription mt={2}>{foldersError}</AlertDescription>
          </Alert>
          <Button colorScheme="brand" onClick={handleRetry}>
            Try again
          </Button>
        </VStack>
      </Center>
    )
  }

  return <>{children}</>
}

export default function ProtectedApp() {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" color="brand.500" />
      </Center>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return (
    <OrgDataProvider>
    <FavoritesProvider>
      {/* Centralized data loader for Redux */}
      <ReduxDataLoader />

      {/* NavigationProvider removed - using Redux store now */}
      <InitialDataGate>
      <Routes>
        {/* Nested routes under DashboardLayout */}
        <Route path="/" element={<DashboardLayoutPage />}>
          <Route index element={<DashboardContent />} />
          <Route path="dashboard" element={<DashboardContent />} />
          <Route path="search" element={<SearchContent />} />
          <Route path="recently-viewed" element={<RecentlyViewedContent />} />
          <Route path="shared-with-me" element={<SharedWithMePage />} />
          <Route path="favorites" element={<FavoritesPage />} />
          <Route path="trash" element={<TrashContent />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="account/:tab" element={<AccountPage />} />
          {/* Legacy routes — the profile/security pages merged into /account */}
          <Route path="profile" element={<Navigate to="/account/usage" replace />} />
          <Route path="profile/security" element={<Navigate to="/account/security" replace />} />
          <Route path="profile/security/audit-log" element={<Navigate to="/account/security" replace />} />
          <Route path="help" element={<HelpPage />} />
          {createProtectedRoute(['super'], user, 'admin/dashboard', <AdminDashboardPage />)}
          {createProtectedRoute(['super'], user, 'admin/organizations', <AdminOrganizationsPage />)}
          {createProtectedRoute(
            ['super'],
            user,
            'admin/organizations/create',
            <CreateOrganizationPage />
          )}
          {createProtectedRoute(
            ['super'],
            user,
            'admin/organizations/:id',
            <OrganizationDetailsPage />
          )}
          {createProtectedRoute(
            ['super'],
            user,
            'admin/organizations/:id/edit',
            <CreateOrganizationPage />
          )}
          {createProtectedRoute(['super'], user, 'admin/plans', <AdminPlansPage />)}
          {createProtectedRoute(
            ['admin', 'super'],
            user,
            'organization/invitations',
            <OrganizationInvitationsPage />
          )}
          {createProtectedRoute(
            ['admin', 'super'],
            user,
            'organization/groups',
            <GroupsPage />
          )}
          {/* File preview shares the app shell so the folder tree stays visible */}
          <Route path="preview/:fileId" element={<FilePreviewContent />} />
        </Route>

        {/* 404 page - separate layout */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </InitialDataGate>
    </FavoritesProvider>
    </OrgDataProvider>
  )
}
